import React, { useState, useEffect } from 'react';

interface QueuedAction {
    id: string;
    timestamp: string;
    type: string;
    url: string;
    method: string;
    headers: Record<string, string>;
    body: string;
}

interface ServiceWorkerMessage {
    type: string;
    [key: string]: unknown;
}

// Offline Support Utility
class OfflineManager {
    private isOnline: boolean;
    private pendingActions: QueuedAction[];

    constructor() {
        this.isOnline = navigator.onLine;
        this.pendingActions = [];
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        // Online/offline events
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.handleOnline();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            this.handleOffline();
        });

        // Service worker events
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
                this.handleServiceWorkerMessage(event.data as ServiceWorkerMessage);
            });
        }
    }

    private handleOnline(): void {
        this.syncPendingActions();
    }

    private handleOffline(): void {
        // Actions will be queued
    }

    private async syncPendingActions(): Promise<void> {
        if (this.pendingActions.length === 0) return;

        const actions = [...this.pendingActions];
        this.pendingActions = [];

        for (const action of actions) {
            try {
                await this.executeAction(action);
            } catch (error) {
                // Re-queue failed actions
                this.pendingActions.push(action);
            }
        }
    }

    private async executeAction(action: QueuedAction): Promise<Response> {
        const response = await fetch(action.url, {
            method: action.method,
            headers: action.headers,
            body: action.body
        });

        if (!response.ok) {
            throw new Error(`Action failed: ${response.status}`);
        }

        return response;
    }

    private queueAction(action: Omit<QueuedAction, 'id' | 'timestamp'>): void {
        const queuedAction: QueuedAction = {
            id: Date.now().toString(),
            timestamp: new Date().toISOString(),
            ...action
        };

        this.pendingActions.push(queuedAction);

        // Try to sync immediately if online
        if (this.isOnline) {
            this.syncPendingActions();
        }
    }

    private handleServiceWorkerMessage(data: ServiceWorkerMessage): void {
        switch (data.type) {
            case 'SYNC_COMPLETE':
                break;
            case 'SYNC_FAILED':
                break;
        }
    }

    // Public API methods
    async saveCustomer(customerData: Record<string, unknown>): Promise<Response | { offline: boolean }> {
        const action = {
            type: 'SAVE_CUSTOMER',
            url: '/api/customers',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(customerData)
        };

        if (this.isOnline) {
            try {
                return await this.executeAction(action as unknown as QueuedAction);
            } catch (error) {
                this.queueAction(action);
                throw error;
            }
        } else {
            this.queueAction(action);
            return Promise.resolve({ offline: true });
        }
    }

    async saveService(serviceData: Record<string, unknown>): Promise<Response | { offline: boolean }> {
        const action = {
            type: 'SAVE_SERVICE',
            url: '/api/services',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(serviceData)
        };

        if (this.isOnline) {
            try {
                return await this.executeAction(action as unknown as QueuedAction);
            } catch (error) {
                this.queueAction(action);
                throw error;
            }
        } else {
            this.queueAction(action);
            return Promise.resolve({ offline: true });
        }
    }

    async saveStock(stockData: Record<string, unknown>): Promise<Response | { offline: boolean }> {
        const action = {
            type: 'SAVE_STOCK',
            url: '/api/stock',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(stockData)
        };

        if (this.isOnline) {
            try {
                return await this.executeAction(action as unknown as QueuedAction);
            } catch (error) {
                this.queueAction(action);
                throw error;
            }
        } else {
            this.queueAction(action);
            return Promise.resolve({ offline: true });
        }
    }

    async saveVadeHatirlatma(vadeData: Record<string, unknown>): Promise<Response | { offline: boolean }> {
        const action = {
            type: 'SAVE_VADE_HATIRLATMA',
            url: '/api/vade-hatirlatmalari',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(vadeData)
        };

        if (this.isOnline) {
            try {
                return await this.executeAction(action as unknown as QueuedAction);
            } catch (error) {
                this.queueAction(action);
                throw error;
            }
        } else {
            this.queueAction(action);
            return Promise.resolve({ offline: true });
        }
    }

    getPendingActionsCount(): number {
        return this.pendingActions.length;
    }

    getPendingActions(): QueuedAction[] {
        return [...this.pendingActions];
    }

    clearPendingActions(): void {
        this.pendingActions = [];
    }

    isOffline(): boolean {
        return !this.isOnline;
    }
}

// Create singleton instance
const offlineManager = new OfflineManager();

// React Hook for offline status
export const useOfflineStatus = (): { isOffline: boolean; pendingCount: number } => {
    const [isOffline, setIsOffline] = useState<boolean>(offlineManager.isOffline());
    const [pendingCount, setPendingCount] = useState<number>(offlineManager.getPendingActionsCount());

    useEffect(() => {
        const updateStatus = (): void => {
            setIsOffline(offlineManager.isOffline());
            setPendingCount(offlineManager.getPendingActionsCount());
        };

        window.addEventListener('online', updateStatus);
        window.addEventListener('offline', updateStatus);

        return () => {
            window.removeEventListener('online', updateStatus);
            window.removeEventListener('offline', updateStatus);
        };
    }, []);

    return { isOffline, pendingCount };
};

// Offline Indicator Component
export const OfflineIndicator: React.FC = () => {
    const { isOffline, pendingCount } = useOfflineStatus();

    if (!isOffline && pendingCount === 0) {
        return null;
    }

    return (
        <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 9999,
            backgroundColor: isOffline ? '#dc3545' : '#28a745',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
            {isOffline ? (
                <>
                    📡 Çevrimdışı
                    {pendingCount > 0 && ` (${pendingCount} bekleyen)`}
                </>
            ) : (
                <>
                    ✅ Çevrimiçi
                    {pendingCount > 0 && ` (${pendingCount} senkronize ediliyor)`}
                </>
            )}
        </div>
    );
};

export default offlineManager;

